import { Box, Card, Skeleton, Stack, Typography } from '@mui/material';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { SectionHeader } from '../../components/SectionHeader';
import { SubtleSurface } from '../../components/SubtleSurface';
import { useStudyPeriodSection } from '../../hooks/useStudyPeriodSection';
import type { StudyPeriodSectionProps } from '../../types';
import { formatIsoDate } from '../../utils/formatIsoDate';

export const StudyPeriodSection = ({ isLoading = false }: StudyPeriodSectionProps) => {
  const {
    sectionLabel,
    isSet,
    startDate,
    endDate,
    dateRangeLabel,
    notSetTitle,
    notSetHint,
    isLoading: isStudyPeriodLoading,
  } = useStudyPeriodSection();

  const showSkeleton = isLoading || isStudyPeriodLoading;

  return (
    <Card
      variant="outlined"
      sx={{
        // Figma: section card padding = spacing-6 (24px).
        p: 6,
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        // Figma: 24px title-to-content gap.
        gap: 6,
        minWidth: 0,
      }}
    >
      {!showSkeleton && !isSet ? (
        // Empty state (Figma): no section header, no inner surface — a centered
        // heading + hint explaining the period is inherited from the cohort,
        // directly on the white section card. `flex: 1` fills the min-height.
        <Box
          sx={{
            p: 4,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            textAlign: 'center',
          }}
          data-testid="ad-study-period-empty"
        >
          {/* Figma empty-state heading: h5 (24px) regular weight. */}
          <Typography variant="h5">{notSetTitle}</Typography>
          {/* Figma body: body1 (16px) secondary. */}
          <Typography variant="body1" color="text.secondary">
            {notSetHint}
          </Typography>
        </Box>
      ) : (
        <>
          <SectionHeader label={sectionLabel} />
          {showSkeleton ? (
            <Skeleton
              variant="rounded"
              height={80}
              animation="wave"
              sx={{ borderRadius: `${borderRadius.md}px` }}
              data-testid="ad-study-period-loading"
            />
          ) : (
            <SubtleSurface
              // Figma READ ONLY VIEW box: 24px/16px padding, 48px content row
              // (matches the cohort study-period box).
              sx={{
                paddingX: 6,
                paddingY: 4,
                minHeight: 80,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
              }}
              data-testid="ad-study-period-card"
            >
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {dateRangeLabel}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {`${formatIsoDate(startDate)} – ${formatIsoDate(endDate)}`}
                </Typography>
              </Stack>
            </SubtleSurface>
          )}
        </>
      )}
    </Card>
  );
};
